// Cliente HTTP minimal contra apps/api.
// Todos los requests llevan credentials: 'include' para que la cookie de sesión
// (inventario_session, httpOnly, SameSite=None) viaje cross-site dentro del iframe.

export const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Dedup de /auth/refresh: cuando el JWT expira, todas las queries activas
// reciben 401 simultáneo. Sin dedup cada una dispara su propio /refresh; el
// segundo encuentra el refresh ya rotado → reuse detection → family revoked
// → user perdió la sesión. Compartir el mismo Promise en flight cierra esa
// race. Path=/auth/refresh hace que el browser solo mande la cookie aquí.
let refreshing: Promise<boolean> | null = null;
function refreshOnce(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

// ===== Auth-failure listener (F7-B session timeout) =====
//
// Cuando un request recibe 401 Y el refresh también falla, llamamos a los
// listeners registrados (típicamente Layout.tsx → modal "Sesión expirada").
// Decisión de diseño: NO podemos detectar `exp` proactivamente porque la
// cookie es httpOnly. Reactivamos solo cuando un request real falla, lo cual
// también cubre el caso de invalidación remota (admin revoca al user).
//
// Guard contra falsos positivos: ignoramos 401 en `/auth/refresh` (ya filtrado
// por el caller) y en `/auth/me` (puede ser el primer fetch sin sesión previa,
// no es "expirada" — es "nunca hubo").
type AuthFailureListener = () => void;
const authFailureListeners = new Set<AuthFailureListener>();
let authFailureFired = false;

export function onAuthFailure(listener: AuthFailureListener): () => void {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
}

/** Notifica una vez (debounce de re-entries paralelos). El flag se reset
 *  cuando algún request vuelve a ser 2xx — eso indica que el user reconectó. */
function fireAuthFailure(path: string): void {
  // /auth/me al boot puede ser 401 sin sesión previa — no es "expirada".
  if (path === '/auth/me') return;
  if (authFailureFired) return;
  authFailureFired = true;
  for (const l of authFailureListeners) {
    try {
      l();
    } catch {
      // listener errors no rompen al request.
    }
  }
}

/** Reset del flag al observar un response OK (user reconectó). */
function resetAuthFailure(): void {
  if (authFailureFired) authFailureFired = false;
}

async function rawFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res = await rawFetch(path, init);

  // 401 + el path no es ya el de refresh → intentar rotación silenciosa.
  // /auth/refresh fallando se trata como 401 final (sin loop infinito).
  if (res.status === 401 && path !== '/auth/refresh') {
    const ok = await refreshOnce();
    if (ok) {
      res = await rawFetch(path, init);
    }
  }

  // F7-B: 401 post-refresh = sesión definitivamente expirada → notificamos
  // listeners (Layout muestra modal). /auth/refresh queda fuera para no
  // loopear (un 401 propio del refresh es un evento distinto ya manejado).
  if (res.status === 401 && path !== '/auth/refresh') {
    fireAuthFailure(path);
  } else if (res.ok) {
    resetAuthFailure();
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const msg =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  // PUT: igual que POST/PATCH pero con method=PUT. Algunos endpoints REST (e.g.
  // pricing.setItem) prefieren PUT para upsert idempotente por (priceListId,
  // variantId). Body opcional para mantener simetría con post/patch.
  // Nota: pasamos el cuerpo aunque sea `{}` cuando el caller lo necesita;
  // `body ? ...` se cumple en objetos vacíos también porque `{}` es truthy.
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  // Upload de archivos via FormData. NO setea Content-Type explícitamente
  // (el browser lo arma con el boundary correcto del multipart).
  // `extra` permite agregar campos no-binarios (e.g. variantId) al multipart.
  upload: async <T>(
    path: string,
    file: File,
    fieldName = 'file',
    extra?: Record<string, string | null | undefined>,
  ): Promise<T> => {
    const fd = new FormData();
    fd.append(fieldName, file);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        if (v !== null && v !== undefined) fd.append(k, v);
      }
    }
    const doUpload = () =>
      fetch(`${API_BASE}${path}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
    let res = await doUpload();
    if (res.status === 401) {
      const ok = await refreshOnce();
      if (ok) res = await doUpload();
    }
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = text;
      }
    }
    if (!res.ok) {
      const msg =
        typeof body === 'object' && body !== null && 'message' in body
          ? String((body as { message: unknown }).message)
          : `HTTP ${res.status}`;
      throw new ApiError(res.status, msg, body);
    }
    return body as T;
  },
};

// ===== Tipos compartidos con el backend =====

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface Tenant {
  id: string;
  name: string;
  /** Slug público del tenant (URLs storefront). Puede ser null en tenants
   *  pre-V3.1 que aún no tienen slug asignado. */
  slug: string | null;
  whatsfyAccountId: string;
  locale: string | null;
  timezone: string | null;
  connected: boolean;
}

// ===== F4-E: tipos de TenantConfig =====
//
// Espejo del shape expuesto por TenantConfigService.get/update en el backend
// (apps/api/src/lib/tenant-config.service.ts). El frontend lo consume vía
// GET /auth/tenant-settings + lo edita en /configuracion.

export interface DocumentValidation {
  pattern: string; // regex string sin /.../
  label: string;
  countryCode: string; // ISO 3166-1 alpha-2
}

export interface PhoneValidation {
  pattern: string;
  countryCode?: string;
}

export interface NumberFormatConfig {
  decimalSeparator: '.' | ',';
  thousandsSeparator: '.' | ',' | ' ';
}

export interface EmailNotifications {
  fromName: string;
  replyTo?: string;
}

export interface Brand {
  name: string;
  logoUrl: string;
  primaryColor?: string;
}

/**
 * V3.3+ — Términos y Condiciones del comerciante. El storefront los expone
 * vía `getTenantBySlug` para que el bot Whatsfy los presente como disclaimer
 * al onboarding del chat (oversell, demoras de envío). `updatedAt` lo escribe
 * el backend automáticamente al PATCH. Todos `null` significa "no configurado"
 * — el bot omite el disclaimer.
 */
export interface LegalTermsAndConditions {
  url: string | null;
  text: string | null;
  updatedAt: string | null;
}

export interface Legal {
  termsAndConditions: LegalTermsAndConditions;
  /**
   * Wave C SA-B — Si `true`, los endpoints públicos del bot (manual-pending,
   * manual-pending-bot) EXIGEN que el cliente haya aceptado los T&C antes de
   * crear el pedido. Default `false` preserva backward-compat con flows que
   * aún no recolectan el accept en la conversación. Compliance ley CO
   * 1581/2012; activar cuando el bot tenga el checkbox conectado.
   */
  botRequiresExplicitConsent: boolean;
}

/**
 * V3.7 SA-C — Estado del wizard Setup del bot Whatsfy persistido como JSONB
 * en `tenant.settings.botSetupProgress`. Mirror exacto del shape `BotSetupProgress`
 * del backend (apps/api/src/lib/tenant-config.service.ts). Shape APLANADA
 * (no anidada en 3 niveles) para que el deepMerge del backend funcione.
 *
 * Todos los campos son individualmente opcionales. Tenants pre-V3.5 NO tienen
 * los smoke /aiFunctions  y tenants pre-V3.7 NO tienen los prompt  /
 * childFlowsConfigured. El frontend trata `botSetupProgress` como
 * `undefined` o parcial.
 *
 * Subdivisión:
 *  - Smoke E2E (legacy V3.5): smoke* — escritos server-side por
 *    POST /api/admin/bot-setup/mark-smoke-complete.
 *  - AI Functions tester (legacy V3.5): aiFunctions* — escritos por el
 *    FunctionTesterService al ejecutar exitoso.
 *  - Prompt template (V3.7 SA-C): prompt* — el wizard
 *    /configuracion → Bot Whatsfy los persiste vía PATCH /auth/tenant-settings.
 *    `promptComputed` es server-computed: el frontend lo lee, NO lo envía.
 *    `promptCustomOverride: null` = clear override (vuelve al template).
 *  - Child flows: `childFlowsConfigured[]` snapshot deduplicado cap 20.
 *
 * Notas de tipos vs SA-A/B:
 *  - `promptTemplateVersion` es **string** ("1.0.0"), NO number — semver-style.
 *  - Backend NO emite valores `null` para los campos opcionales (los omite del
 *    JSON). El `| null` en los tipos `*Input` debajo es semántica de "limpiar
 *    override" cuando se manda al PATCH, no algo que se lee.
 */
export interface BotSetupProgress {
  // ─── Smoke E2E (legacy V3.5) ────────────────────────────────────────────
  smokeLastRunAt?: string;
  smokeLastRunBy?: string | null;
  smokeLastNotes?: string | null;
  // ─── AI Functions tester (legacy V3.5) ──────────────────────────────────
  aiFunctionsLastTestedAt?: string;
  aiFunctionsLastTested?: string;
  aiFunctionsTested?: string[];
  aiFunctionsPassed?: number;
  // ─── Prompt template (V3.7 SA-C) ────────────────────────────────────────
  promptTemplateId?: string;
  promptTemplateVersion?: string;
  promptTemplateValues?: Record<string, string>;
  promptCustomOverride?: string | null;
  promptComputed?: string;
  promptUpdatedAt?: string;
  // ─── Child flows (V3.7 SA-C) ────────────────────────────────────────────
  childFlowsConfigured?: string[];
  // ─── Inactividad / Re-engagement (V3.7 SA-K, step 7) ────────────────────
  // Boolean toggle del comerciante: "ya configuré la Drip Campaign en
  // ChatRace". El módulo NO verifica la campaign.
  inactivitySequenceConfigured?: boolean;
  // ─── Keywords ultra-rápidas (V3.7 SA-K, step 8) ─────────────────────────
  // Lista de keywords activadas (subset del whitelist canónico del backend
  // `SUGGESTED_KEYWORDS`). Snapshot, NO append.
  keywordsConfigured?: string[];
}

/**
 * Input parcial del progreso para PATCH /auth/tenant-settings. Subset del
 * shape de lectura: los campos legacy (smoke /aiFunctions ) NO se mandan
 * porque los escribe el backend directo desde otros endpoints. `null` en
 * los 5 campos `prompt*` (excepto `promptUpdatedAt`) = "clear override"
 * (delete del JSONB). `promptComputed` se acepta transitional — TODO(SA-D)
 * moverlo a render server-side cuando aterrice el template registry.
 */
export interface UpdateBotSetupProgressInput {
  promptTemplateId?: string | null;
  promptTemplateVersion?: string | null;
  promptTemplateValues?: Record<string, string> | null;
  promptCustomOverride?: string | null;
  promptComputed?: string | null;
  promptUpdatedAt?: string;
  childFlowsConfigured?: string[];
  // V3.7 SA-K — toggle step 7 + snapshot step 8. Mismo patrón merge atómico
  // que childFlowsConfigured (backend dedupea + cap 20).
  inactivitySequenceConfigured?: boolean;
  keywordsConfigured?: string[];
}

/**
 * PRICE-C: `pricingMode` define cómo se visualizan los precios del tenant.
 * - 'single': una sola moneda. La UI oculta selectores de PriceList y simplifica.
 * - 'multi': varias monedas. Listas de precios visibles, columnas extra.
 *
 * `pricingModeExplicit` indica si el comerciante eligió explícitamente
 * (vía onboarding modal o toggle en /configuracion). Si es false, la UI
 * dispara el onboarding modal. Si PRICE-A merged, `pricingMode` puede venir
 * inferido (no explícito) cuando el tenant tiene 0 ó 1 PriceLists activas.
 */
export type PricingMode = 'single' | 'multi';

export interface ResolvedTenantConfig {
  tenantId: string;
  locale: string;
  timezone: string;
  defaultCurrency: string;
  documentValidation: DocumentValidation;
  phoneValidation: PhoneValidation;
  numberFormat: NumberFormatConfig;
  emailNotifications: EmailNotifications;
  brand: Brand;
  // V3.3+ — Términos y Condiciones del tenant. Optional para backward-compat
  // con backends pre-V3.3 (que no emiten el campo). El form de /configuracion
  // arranca con defaults null cuando legal es undefined.
  legal?: Legal;
  adminWhatsappContactId?: string;
  // PRICE-A backend siempre devuelve `pricingMode`. 'single' oculta la UI
  // de PriceLists; 'multi' la habilita. `pricingModeExplicit` (PRICE-C) =
  // true si fue seteado vía PATCH; false si fue inferido por count(PriceLists).
  // Onboarding modal usa `pricingModeExplicit=false` como señal de "tenant
  // aún no decidió".
  pricingMode?: PricingMode;
  pricingModeExplicit?: boolean;
  // V3.2 SA3: campos top-level del tenant para storefront público. Optional —
  // backends pre-V3.2 no los emiten desde /auth/tenant-settings y el form
  // entonces arranca con strings vacíos. La fuente de verdad sigue siendo el
  // PATCH a `/auth/tenant-settings/storefront`.
  slug?: string | null;
  landingConfig?: LandingConfigDraft | null;
  // V3.7 — progreso del wizard del bot. Persistido como JSONB en
  // tenant.settings.botSetupProgress por SA-C. Optional para backward-compat
  // con backends pre-V3.7 (el wizard renderea defaults).
  botSetupProgress?: BotSetupProgress;
  // V3.9 — permisos configurables (acciones destructivas). El backend siempre
  // emite `permissions` con default. Optional acá para backward-compat con
  // backends pre-V3.9 (los componentes tratan undefined como "admin permitido").
  permissions?: Permissions;
}

// V3.9 — permisos del tenant. `adminCanHardDelete` (default true) controla si
// los admin pueden hard-deletear; solo el owner lo cambia (gate en el backend).
export interface Permissions {
  adminCanHardDelete?: boolean;
}

// Shape de PATCH /auth/tenant-settings.
// `replyTo` y `primaryColor` admiten null (semántica "limpiar override") en
// el wire — el backend los normaliza a undefined antes de delegar al service.
export interface UpdateEmailNotificationsInput {
  fromName?: string;
  replyTo?: string | null;
}
export interface UpdateBrandInput {
  name?: string;
  logoUrl?: string;
  primaryColor?: string | null;
}
// V3.3+ — input parcial de T&C. `url` y `text` admiten null para limpiar.
// `updatedAt` lo setea el backend al ISO timestamp si cambia algún campo.
export interface UpdateLegalTermsAndConditionsInput {
  url?: string | null;
  text?: string | null;
}
export interface UpdateLegalInput {
  termsAndConditions?: UpdateLegalTermsAndConditionsInput;
  /**
   * Wave C SA-B — toggle del gate del bot path. `undefined` = no tocar
   * (preserva el valor actual); `boolean` set explícito. Default backend:
   * `false` cuando la key no existe en JSONB.
   */
  botRequiresExplicitConsent?: boolean;
}
export interface UpdateTenantSettingsInput {
  name?: string;
  locale?: string;
  timezone?: string;
  defaultCurrency?: string;
  documentValidation?: Partial<DocumentValidation>;
  phoneValidation?: Partial<PhoneValidation>;
  numberFormat?: Partial<NumberFormatConfig>;
  emailNotifications?: UpdateEmailNotificationsInput;
  brand?: UpdateBrandInput;
  legal?: UpdateLegalInput;
  // V3.9 — solo el owner puede mandar esto (el backend rechaza 403 si admin).
  permissions?: { adminCanHardDelete?: boolean };
  adminWhatsappContactId?: string | null;
  // PRICE-A/C: setear el modo de precios. Toggle desde /configuracion o
  // primera elección via onboarding modal.
  pricingMode?: PricingMode;
  // V3.7 — progreso del wizard del bot. Patch parcial; el backend hace merge
  // sobre el JSONB existente (passing `promptCustomOverride: null` limpia).
  botSetupProgress?: UpdateBotSetupProgressInput;
}

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  city: string | null;
  address: string | null;
  kind: 'bodega' | 'tienda' | 'oficina';
  active: boolean;
  // Mapeo a IDs de canales externos: { shopify: "gid://...", tiendanube: "..." }.
  // Se asocian via PATCH /api/warehouses/:id/external-ids.
  externalIds?: Record<string, string>;
  // SA10/SA13 — profile semántico para routing automático + flag
  // internacional. Opcionales en el shape porque el backend los expone
  // condicionalmente y los tenants pre-F4 no los tienen seteados.
  profile?: 'principal' | 'pdv' | 'internacional' | null;
  acceptsInternational?: boolean;
  createdAt: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
}

export interface Supplier {
  id: string;
  name: string;
  city: string | null;
  leadDays: number | null;
  rating: string | null;
}

export type ProductMode = 'simple' | 'numeric' | 'variants' | 'bundle';

export interface Variant {
  id: string;
  productId: string;
  sku: string;
  label: string | null;
  barcode: string | null;
  imageUrl?: string | null;
  /** V3.3.2 — foto plana del color/tejido para el swatch del VariantSelector.
   *  Separado de `imageUrl` (foto del modelo usando la variante). */
  swatchImageUrl?: string | null;
  weight?: string | null;
  active?: boolean;
  /** Override de cost/price por variante. NULL = hereda del Product.
   *  Los Decimal de Prisma se serializan como string (mantiene precisión). */
  cost?: string | null;
  price?: string | null;
  /** Dimensiones en cm. NULL = no definidas. Shape: {w?, h?, d?}. */
  dimensions?: { w?: number; h?: number; d?: number } | null;
  /** Atributos estructurados (e.g. {color:"rojo", talle:"M"}). Default `{}`
   *  cuando la variant fue creada antes del soporte de atributos. */
  attributes?: Record<string, string>;
  /** Inventory levels por bodega — solo poblado por `GET /api/products/:id`
   *  (Prisma `include: { inventoryLevels: true }`). En el listado paginado
   *  `GET /api/products` viene undefined. */
  inventoryLevels?: Array<{
    id: string;
    qty: number;
    warehouseId: string;
    reorderPoint: number | null;
  }>;
}

export interface ProductImage {
  id: string;
  productId: string;
  variantId: string | null;
  /** Display image (1024×1024 webp, ~80kB). Para detalle del producto y push a canales. */
  url: string;
  /** Thumbnail (256×256 webp, ~10kB). Para grids/listados. Null si la imagen es vieja
   *  pre-bloque-9 (antes de sharp). En ese caso usar `url` como fallback. */
  thumbnailUrl: string | null;
  /** Medium-size variant (~512×512 webp, ~30-40kB). Devuelto por el backend
   *  cuando existe; usado por listados y modales que renderizan thumbnails más
   *  grandes (160-200px). Optional — solo si el backend lo serializa. Fallback
   *  a `thumbnailUrl` (256px) y luego a `url` (1024px). */
  mediumUrl?: string | null;
  s3Key: string;
  position: number;
  alt: string | null;
  bytes: number;
  mimeType: string;
  createdAt: string;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  supplierId: string | null;
  mode: ProductMode;
  cost: string | null;
  price: string | null;
  active: boolean;
  imageUrl: string | null;
  category?: { id: string; name: string } | null;
  supplier?: { id: string; name: string } | null;
  _count?: { variants: number };
  variants?: Variant[];
  images?: ProductImage[];
  /**
   * V3.3.3 — stock agregado de todas las variantes en bodegas activas.
   * Devuelto por GET /api/products en el listing admin. Reemplaza el cómputo
   * client-side que sumaba `useInventoryLevels()` (rompía con > 100 levels).
   * Endpoints unitarios (GET /api/products/:id) NO lo incluyen.
   */
  totalQty?: number;
}

export interface InventoryLevel {
  id: string;
  variantId: string;
  warehouseId: string;
  qty: number;
  reorderPoint: number | null;
  variant: { id: string; sku: string; label: string | null; productId: string };
  warehouse: { id: string; code: string; name: string };
}

export interface AuditEntry {
  id: string;
  /** Tipo de entidad: 'product', 'variant', 'inventory_level', etc. */
  entityType: string;
  entityId: string;
  userId: string | null;
  /** El backend lo incluye cuando puede hacer JOIN con users. */
  userEmail?: string | null;
  /** 'create' | 'update' | 'delete' | 'archive' | 'price_change' | etc. */
  action: string;
  /** Diff por campo: { name: { before, after }, ... }. */
  changes: Record<string, { before: unknown; after: unknown }>;
  meta: Record<string, unknown>;
  createdAt: string;
}

export type MovementKind = 'entrada' | 'salida' | 'transferencia' | 'ajuste' | 'merma';

export interface Movement {
  id: string;
  variantId: string;
  warehouseId: string;
  kind: MovementKind;
  qty: number;
  reason: string | null;
  balanceAfter: number;
  createdAt: string;
  variant?: { id: string; sku: string; label: string | null };
  warehouse?: { id: string; code: string; name: string };
  /** Email del user que disparó el movement. null cuando viene de webhooks/cron. */
  user?: { email: string | null } | null;
}

// ===== API keys (Sprint 1 bot+checkout) =====
//
// Tokens de autenticación para integraciones externas (bots/checkout/flows).
// El secret completo solo aparece en el response del POST de creación; nunca
// más se devuelve. El UI debe forzar al user a copiarlo en ese momento.
export interface ApiKey {
  id: string;
  /** Prefijo público del token (ej. "e7f2a3b1" — 8 chars hex; la marca
   *  wfy_inv_ se agrega al armar el fullToken). Seguro de mostrar. */
  prefix: string;
  label: string;
  /** Lista de scopes habilitados, ej. ['stock:read', 'orders:write']. */
  scopes: string[];
  /** V3 — array de warehouseCodes a los que esta key tiene acceso. `[]`
   *  significa todas las bodegas (backward compat). Si el array tiene
   *  códigos, ApiKeyGuard restringe stock/products/payments a esas bodegas. */
  warehouseScopes: string[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  createdById: string | null;
}

export interface ApiKeyCreated extends ApiKey {
  /** Solo presente en la response del POST. PLAINTEXT — display once. */
  secret: string;
  /** Helper armado por el cliente: `wfy_inv_${prefix}_${secret}`. */
  fullToken: string;
}

// ===== Payments (Sprint 4 — pago por transferencia + verificación humana) =====
//
// Modelo: el bot acumula carrito + recibe pantallazo del cliente y crea un
// PendingPayment via API pública. El admin lo verifica/rechaza desde el panel.
// Detalle de arquitectura en docs/research/2026-05-08-bank-transfer-architecture.md.

export type PaymentMethodType = 'bank_transfer' | 'bre_b' | 'cash' | 'other';

export type PendingPaymentStatus =
  | 'pending_screenshot'
  | 'pending_verification'
  | 'verified'
  | 'verified_with_oversell'
  | 'rejected'
  | 'expired';

/** Razón canónica del rechazo. 'other' exige note libre obligatoria. */
export type PaymentRejectReason =
  | 'amount_mismatch'
  | 'illegible'
  | 'invalid_date'
  | 'duplicate'
  | 'not_received'
  | 'other';

/** Canal omnicanal del cliente final que envía el pedido al bot (F2-A).
 *  Discriminador del canal de mensajería, NO de la integración técnica
 *  (`ChannelKind` shopify/woocommerce/etc se mantiene aparte). El enum
 *  espejea exactamente el schema Prisma `OriginChannelKind`. */
export type OriginChannelKind =
  | 'whatsapp'
  | 'messenger'
  | 'instagram'
  | 'telegram'
  | 'webchat'
  | 'viber'
  | 'sms'
  | 'tiktok'
  | 'other';

/** Datos de envío estructurados asociados a un PendingPayment (F2-A).
 *  Modelo con FK XOR contra PendingPayment / DraftOrderRequest — desde el
 *  panel admin solo vemos el lado de PendingPayment. PII: `customerIdNumber`
 *  y `customerEmail` son datos personales — la UI los muestra default pero
 *  con toggle "ocultar". */
export interface ShippingInfo {
  id: string;
  /** ID/cédula del cliente. Validación CO regex en backend. */
  customerIdNumber: string | null;
  customerEmail: string | null;
  street: string;
  streetNumber: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string;
  /** Departamento (en CO). */
  state: string;
  postalCode: string | null;
  /** ISO country code. Default 'CO'. */
  country: string;
  /** 'servientrega'|'coordinadora'|'interrapidisimo'|'472'|'other'. */
  carrierPref: string | null;
  notes: string | null;
  // --- V3.3 tracking (SA1/SA5) ---
  /** Número de guía emitido por la transportadora. NULL hasta que el admin
   *  registra el envío (manual o vía generate-label). */
  trackingNumber?: string | null;
  /** URL pública de rastreo (el cliente sigue su envío). */
  trackingUrl?: string | null;
  /** Carrier real con el que se despachó. Puede diferir de `carrierPref`
   *  si el admin terminó usando otro proveedor. */
  shippingCarrier?: string | null;
  /** Costo cobrado por el envío. */
  shippingCost?: number | null;
  /** ISO 4217 del shippingCost. */
  shippingCurrency?: string | null;
  /** ISO timestamp del despacho. */
  shippedAt?: string | null;
  /** ISO timestamp de la entrega confirmada. */
  deliveredAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Cuenta/método al que el cliente final transfiere. NO contiene credenciales —
 *  son datos públicos que el merchant le pasa al cliente vía bot. */
export interface PaymentMethod {
  id: string;
  type: PaymentMethodType;
  /** Etiqueta corta para UI admin. El cliente final ve `bankName + accountNumber`. */
  displayName: string;
  bankName: string | null;
  accountNumber: string | null;
  /** "ahorros" / "corriente" / null. Texto libre. */
  accountType?: string | null;
  accountHolderName: string | null;
  /** ID/cédula del titular. Algunos clientes lo piden. */
  accountHolderId?: string | null;
  /** Llave Bre-B (celular, email, alias). Solo type='bre_b'. */
  breBKey: string | null;
  /** Texto libre que el bot inyecta junto con los datos del método. */
  instructions: string | null;
  /** Moneda del método (ISO-4217 3 chars upper, ej. `COP`, `USD`).
   *  NULL = legacy/agnóstico (válido para cualquier currency). Cuando setea,
   *  el storefront filtra los métodos por currency del PriceList del carrito
   *  (Zelle USD solo en carritos USD, Bancolombia COP solo en COP, etc.). */
  currencyCode: string | null;
  isActive: boolean;
  displayOrder: number;
  /** Canales a los que aplica el método. Array vacío = visible en todos los
   *  canales (semántica del backend). Siempre presente como array. */
  channelIds: string[];
  createdAt?: string;
  updatedAt?: string;
}

/** Snapshot del item al momento del pending. Si `variantId` es null, el bot
 *  mandó un SKU que no existe en el catálogo (caso edge — el admin lo verá
 *  para corregir manualmente). */
export interface PendingPaymentItem {
  variantId: string | null;
  sku: string;
  qty: number;
  unitPrice: number;
  productName?: string;
  variantLabel?: string | null;
}

/** Append-only audit trail. kind ∈ {screenshot_uploaded, verified, rejected,
 *  note_added, expired, oversell_detected, reopened}. */
export interface PaymentVerificationLog {
  id: string;
  pendingPaymentId: string;
  /** null cuando viene del bot o cron. Set cuando un admin actuó. */
  userId: string | null;
  userEmail?: string | null;
  kind: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface PendingPayment {
  id: string;
  channelId: string | null;
  draftOrderRequestId: string | null;
  items: PendingPaymentItem[];
  customerEmail: string | null;
  customerPhone: string | null;
  customerName: string | null;
  totalAmount: number;
  currencyCode: string;
  paymentMethod: PaymentMethod | null;
  screenshotUrl: string | null;
  screenshotMimeType: string | null;
  /** Versión optimizada del comprobante (~1200×1200 webp, ~150 kB) para usar
   *  en listados/cards. El `screenshotUrl` original puede ser 4-8 MB (foto de
   *  cámara móvil) y demora en cargar. Optional — backend lo serializa solo
   *  cuando existe. Fallback a `screenshotUrl` directo. NO usar para el
   *  lightbox (necesita full quality). */
  thumbnailUrl?: string | null;
  /** Origen del cliente (e.g. "whatsapp", "instagram", "shopify"). Legacy
   *  string — preferí `originChannelKind` cuando esté poblado (F2-A). */
  originChannel: string | null;
  /** Canal omnicanal first-class (F2-A). NULL en pendings legacy. */
  originChannelKind?: OriginChannelKind | null;
  /** ID nativo del cliente en el canal de origen (psid Messenger, ig_user_id
   *  Instagram, chat_id Telegram, session_id Webchat). PII visible al admin. */
  customerExternalId?: string | null;
  /** Discriminador del canal del externalId. */
  customerExternalIdKind?: string | null;
  // --- Click IDs first-class por plataforma de ads (F2-A) ---
  /** Meta Click-to-WhatsApp click ID. */
  ctwaClid?: string | null;
  /** TikTok Ads click ID. */
  ttclid?: string | null;
  /** Google Ads click ID. */
  gclid?: string | null;
  /** Pinterest click ID (futuro). */
  pclid?: string | null;
  // --- Atribución general del ad/post de origen (F2-A) ---
  adId?: string | null;
  campaignId?: string | null;
  adHeadline?: string | null;
  adBody?: string | null;
  adImageUrl?: string | null;
  /** 'ad' | 'post' | 'SHORTLINK'. */
  referralSourceType?: string | null;
  referralSourceUrl?: string | null;
  /** ref/start/context param genérico (Telegram/Webchat/Viber/CTM/CTD). */
  referralRef?: string | null;
  /** Catch-all (flow_id, product_id, ad_creative_id, ads_context_data, etc.). */
  referralExtras?: Record<string, unknown> | null;
  // --- Datos de envío (F2-A) ---
  /** NULL hasta que el bot o el admin los rellena. */
  shippingInfo?: ShippingInfo | null;
  status: PendingPaymentStatus;
  /** Items que quedaron sin stock al verificar — solo populated cuando
   *  status='verified_with_oversell'. */
  oversellSummary: PendingPaymentItem[] | null;
  rejectedReason: string | null;
  rejectedNote?: string | null;
  notes: string | null;
  createdAt: string;
  expiresAt: string;
  verifiedAt: string | null;
  rejectedAt: string | null;
  /** Solo populated en GET /api/payments/:id (detalle). */
  verificationLogs?: PaymentVerificationLog[];
  /** V3.3.2 — Descuento por volumen aplicado al crear el pending. NULL si la
   *  regla no aplicó o no había VolumeDiscount activo. */
  discountAmount?: number | null;
  /** V3.3.2 — Label legible del descuento (ej. "3+ unidades 10% OFF"). NULL
   *  si no aplica. Persistido para audit trail incluso si la regla se archivó. */
  discountLabel?: string | null;
}

// ===== Pricing (Fase 3 multi-currency) =====

/**
 * Lista de precios denominada en una moneda ISO 4217. Cada tenant arranca con
 * un PriceList default ("COP nacional" en el seed); el comerciante puede
 * crear listas adicionales (USD intl, EUR, etc) y asignarlas a canales para
 * mostrar precios distintos según el canal/audiencia.
 *
 * Solo una lista por tenant puede tener `isDefault=true` (invariante DB con
 * partial unique index). Cuando un canal NO tiene `priceListId`, los lookups
 * caen al default del tenant.
 */
export interface PriceList {
  id: string;
  tenantId: string;
  name: string;
  /** ISO 4217, 3 letras mayúsculas (ej. "COP", "USD"). */
  currencyCode: string;
  isDefault: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  /** Solo populated por el backend cuando se pide `_count`. */
  _count?: { items: number };
}

/**
 * Precio puntual de una variante en una `PriceList`. Ausencia de fila →
 * fallback a `Variant.price` (legacy).
 */
export interface PriceListItem {
  id: string;
  tenantId: string;
  priceListId: string;
  variantId: string;
  /** Decimal serializado como string para preservar precisión. */
  price: string;
  createdAt: string;
  updatedAt: string;
  /** Backend hace JOIN cuando se pide detalle. Útil para el panel admin. */
  variant?: {
    id: string;
    sku: string;
    label: string | null;
    productId: string;
    product?: { id: string; name: string; sku: string };
  };
}

// ===== Webhooks outbound (F5) =====
//
// Endpoints registrados por el comerciante para recibir eventos del módulo
// (payment.verified, inventory.low_stock, etc.). El secret HMAC se muestra
// plaintext UNA sola vez al crear; después solo el shape público viaja al
// frontend. Detalle de arquitectura en ADR-005.

/** Lista canónica de eventos emitidos por el módulo. Mantener sincronizada
 *  con `SUPPORTED_EVENT_TYPES` del backend (webhooks-outbound.service.ts).
 *  Si el backend agrega uno nuevo, sumarlo acá + en el UI de checkboxes. */
export const SUPPORTED_WEBHOOK_EVENTS = [
  'payment.created',
  'payment.verified',
  'payment.rejected',
  'payment.expired',
  'payment.oversell_detected',
  'inventory.low_stock',
  'inventory.out_of_stock',
  'inventory.back_in_stock',
  'draft_order.paid',
  'draft_order.cancelled',
  'reservation.expired',
] as const;

export type WebhookEventType = (typeof SUPPORTED_WEBHOOK_EVENTS)[number];

export type DeliveryStatus =
  | 'pending'
  | 'in_flight'
  | 'delivered'
  | 'failed'
  | 'dead_letter';

export interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Solo presente en la response del POST /api/webhooks. PLAINTEXT del HMAC
 *  — display once. El backend nunca lo vuelve a exponer. */
export interface WebhookEndpointCreated extends WebhookEndpoint {
  plaintextSecret: string;
}

export interface WebhookDelivery {
  id: string;
  endpointId: string;
  eventType: string;
  eventId: string;
  payload: unknown;
  attempts: number;
  status: DeliveryStatus;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  responseCode: number | null;
  responseBody: string | null;
  createdAt: string;
}

// ===== Atribución de conversiones (F6) =====
//
// Plataformas de ads donde el módulo reporta conversiones (Meta CAPI, TikTok,
// Google Ads, Pinterest). El comerciante configura la credencial UNA vez; el
// módulo dispara eventos `Purchase`/`Lead` automáticamente al verificar pagos
// o crear pendings. Detalle de arquitectura en ADR-006.

export const SUPPORTED_CONVERSION_PLATFORMS = [
  'meta',
  'tiktok',
  'google',
  'pinterest',
] as const;

export type ConversionPlatformType =
  (typeof SUPPORTED_CONVERSION_PLATFORMS)[number];

export const SUPPORTED_CONVERSION_EVENTS = [
  'Purchase',
  'Lead',
  'AddToCart',
  'InitiateCheckout',
] as const;

export type ConversionEventName =
  (typeof SUPPORTED_CONVERSION_EVENTS)[number];

export type ConversionEventStatus = 'pending' | 'sent' | 'failed';

export interface ConversionPlatform {
  id: string;
  platform: ConversionPlatformType;
  pixelId: string | null;
  /**
   * Indica que hay una credencial configurada. NUNCA expone el token. Si el
   * merchant quiere validar que rotó correctamente, debe disparar un evento
   * de prueba y mirar el response code.
   */
  hasAccessToken: boolean;
  testEventCode: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConversionEvent {
  id: string;
  platformId: string;
  pendingPaymentId: string | null;
  draftOrderId: string | null;
  eventName: string;
  eventId: string;
  status: ConversionEventStatus;
  attempts: number;
  responseCode: number | null;
  responseBody: string | null;
  createdAt: string;
}

/** Fila del reporte de atribución agrupada por ad. */
export interface AttributionReportItem {
  adId: string;
  adHeadline: string | null;
  campaignId: string | null;
  verifiedCount: number;
  totalValue: number;
  currency: string;
}

/**
 * Total por moneda del reporte de atribución. El módulo NO convierte FX (F3):
 * el backend agrupa los valores por `currency` en vez de sumar un escalar mixto
 * (COP + USD sin sentido).
 */
export interface AttributionTotalByCurrency {
  currency: string;
  totalValue: number;
}

export interface AttributionReportResponse {
  items: AttributionReportItem[];
  totalRows: number;
  totalsByCurrency: AttributionTotalByCurrency[];
}

// ===== V3.2 SA3: storefront config tipos =====
//
// Espejo del shape JSONB que el backend persiste en `Tenant.landingConfig`. El
// derivado (`brandName/brandLogo/brandColor/accentColor`) lo consume
// `storefront.service.ts → deriveBrand`. Las claves `hero*`/`payNowText`/
// `shippingCopy`/`tracking.*` son aditivas — JSONB las acepta y storefront
// pages las leen on-demand.
//
// Todas las propiedades son opcionales y string para mantener el modelo
// permisivo: el comerciante puede llenar parcial y el storefront aplica
// defaults. Tracking pixel IDs son strings opacos (Meta/GA/TikTok los emiten
// con formato propio que no validamos client-side).
export interface LandingConfigTracking {
  metaPixelId?: string;
  gaMeasurementId?: string;
  tiktokPixelId?: string;
}

export interface LandingConfigDraft {
  brandName?: string;
  brandLogo?: string;
  brandColor?: string;
  accentColor?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  heroImage?: string;
  heroVideo?: string;
  payNowText?: string;
  shippingCopy?: string;
  tracking?: LandingConfigTracking;
  // `defaultChannelId` lo edita el backend / otros flujos; lo dejamos
  // tipado por completitud pero el form de SA3 no lo expone.
  defaultChannelId?: string;
}

// Shape de PATCH /auth/tenant-settings/storefront.
// `slug` opcional (regex `^[a-z0-9-]{3,40}$`); `landingConfig` es merge
// shallow del lado del backend (el frontend mandar el shape completo del
// formulario para evitar drift cross-tab).
export interface UpdateTenantStorefrontPayload {
  slug?: string;
  landingConfig?: LandingConfigDraft;
}

// ============================================================================
// VolumeDiscount — descuentos por cantidad ajustables (V3.x).
// ============================================================================
// `kind='percent'` → `value` es 0-100 (string preservando precisión Decimal).
// `kind='fixed'`   → `value` es monto en la currency del PriceList resuelto.
// Scope determinado por la combinación de `productId`/`variantId`:
//   - Ambos null → tenant-wide.
//   - `productId` solo → toda la familia del producto.
//   - Ambos → variant específica.

export type VolumeDiscountKind = 'percent' | 'fixed';

export interface VolumeDiscount {
  id: string;
  productId: string | null;
  variantId: string | null;
  minQty: number;
  kind: VolumeDiscountKind;
  // Decimal viene como string por Prisma — preservamos string para no perder
  // precisión en operaciones de display/edición. Numérico solo al someter.
  value: string;
  label: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVolumeDiscountPayload {
  productId?: string | null;
  variantId?: string | null;
  minQty: number;
  kind: VolumeDiscountKind;
  value: number;
  label?: string | null;
  active?: boolean;
}

export type UpdateVolumeDiscountPayload = Partial<CreateVolumeDiscountPayload>;

interface VolumeDiscountListResponse {
  items: VolumeDiscount[];
}

const VOLUME_DISCOUNTS_BASE = '/api/volume-discounts';

export function listVolumeDiscounts(params?: {
  productId?: string;
  variantId?: string;
  active?: boolean;
}): Promise<VolumeDiscount[]> {
  const qs = new URLSearchParams();
  if (params?.productId) qs.set('productId', params.productId);
  if (params?.variantId) qs.set('variantId', params.variantId);
  if (params?.active !== undefined) qs.set('active', String(params.active));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api
    .get<VolumeDiscountListResponse>(`${VOLUME_DISCOUNTS_BASE}${suffix}`)
    .then((r) => r.items);
}

export function createVolumeDiscount(
  payload: CreateVolumeDiscountPayload,
): Promise<VolumeDiscount> {
  return api.post<VolumeDiscount>(VOLUME_DISCOUNTS_BASE, payload);
}

export function updateVolumeDiscount(
  id: string,
  payload: UpdateVolumeDiscountPayload,
): Promise<VolumeDiscount> {
  return api.patch<VolumeDiscount>(`${VOLUME_DISCOUNTS_BASE}/${id}`, payload);
}

export function deleteVolumeDiscount(id: string): Promise<void> {
  return api.del<void>(`${VOLUME_DISCOUNTS_BASE}/${id}`);
}

/**
 * V3.3.3 — Export "puro datos" del inventario desglosado por bodega.
 *
 * Endpoint dedicado `GET /api/inventory/levels/export`. Una fila por
 * (variant × warehouse) con qty/reservedQty/availableQty/reorderPoint/
 * belowReorderPoint. Distinto al export del catálogo (`/api/products/export`)
 * que solo trae total agregado.
 *
 * Devuelve un Blob — el caller construye el <a> con `URL.createObjectURL`
 * para disparar la descarga (mismo patrón que `Products.tsx` usa inline).
 * Throttle backend: 30/min.
 */
export async function exportInventoryLevels(
  format: 'xlsx' | 'csv',
  opts: {
    warehouseCode?: string;
    includeInactive?: boolean;
  } = {},
): Promise<Blob> {
  const sp = new URLSearchParams();
  sp.set('format', format);
  if (opts.warehouseCode) sp.set('warehouseCode', opts.warehouseCode);
  if (opts.includeInactive) sp.set('includeInactive', 'true');
  const url = `${API_BASE}/api/inventory/levels/export?${sp.toString()}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Error al exportar inventario: HTTP ${res.status}`);
  }
  return res.blob();
}
